// The v3.2 template editor ("Document") edits a template as sections with
// their own instructions, required or optional, plus a style and a length.
// Templates store only a name, headings and one instructions text (the
// "rules" the note prompt gives the highest priority), so the per-section
// guidance, style and length are kept in a short, readable block at the end
// of that text. Templates without the block load as they are (all of their
// text is the general instructions) and are saved unchanged when nothing in
// the block is set.

export type TemplateStyle = "bullets" | "paragraphs" | null;
export type TemplateLength = "concise" | "detailed" | null;

export interface TemplateSection {
  // Stable key for React lists and reordering (not stored).
  key: string;
  heading: string;
  instructions: string;
  optional: boolean;
}

export interface TemplateDocument {
  name: string;
  general: string;
  sections: TemplateSection[];
  style: TemplateStyle;
  length: TemplateLength;
  isDefault: boolean;
}

const START = "[Template settings]";
const END = "[/Template settings]";
const STYLE_TEXT: Record<Exclude<TemplateStyle, null>, string> = {
  bullets: "Style: write every section in bullet points.",
  paragraphs: "Style: write every section in short paragraphs, no bullets.",
};
const LENGTH_TEXT: Record<Exclude<TemplateLength, null>, string> = {
  concise: "Length: keep the note concise.",
  detailed: "Length: make the note detailed.",
};

let counter = 0;
export const sectionKey = (): string => `section-${++counter}`;

const oneLine = (value: string): string => value.replace(/\s+/g, " ").trim();
const quote = (value: string): string => value.replace(/"/g, "'");

export const emptySection = (heading = ""): TemplateSection => ({
  key: sectionKey(),
  heading,
  instructions: "",
  optional: false,
});

/** The block the editor writes, or "" when nothing in it is set. */
export const settingsBlock = (doc: TemplateDocument): string => {
  const lines: string[] = [];
  if (doc.style) lines.push(STYLE_TEXT[doc.style]);
  if (doc.length) lines.push(LENGTH_TEXT[doc.length]);
  for (const section of doc.sections) {
    const heading = oneLine(section.heading);
    const instructions = oneLine(section.instructions);
    if (!heading || (!instructions && !section.optional)) continue;
    const kind = section.optional
      ? "optional, leave it out when it was not discussed"
      : "required";
    lines.push(
      `Section "${quote(heading)}" (${kind})${instructions ? `: ${instructions}` : "."}`,
    );
  }
  return lines.length ? [START, ...lines, END].join("\n") : "";
};

/** The stored instructions text for this document. */
export const toInstructions = (doc: TemplateDocument): string => {
  const block = settingsBlock(doc);
  const general = doc.general.trim();
  if (!block) return doc.general;
  return general ? `${general}\n\n${block}` : block;
};

const SECTION_LINE =
  /^Section "(.+?)" \((required|optional[^)]*)\)(?::\s*(.*)|\.)?$/;

/** Reads a stored template into the editor's document. */
export const fromTemplate = (template: {
  name?: string | null;
  headers?: readonly string[] | null;
  optionalText?: string | null;
  isDefault?: boolean | number | null;
}): TemplateDocument => {
  const text = template.optionalText ?? "";
  let general = text;
  let style: TemplateStyle = null;
  let length: TemplateLength = null;
  const guidance = new Map<
    string,
    { instructions: string; optional: boolean }
  >();
  const start = text.lastIndexOf(START);
  const end = text.lastIndexOf(END);
  if (start !== -1 && end > start) {
    general = (text.slice(0, start) + text.slice(end + END.length)).trim();
    for (const raw of text.slice(start + START.length, end).split("\n")) {
      const line = raw.trim();
      if (!line) continue;
      if (line === STYLE_TEXT.bullets) style = "bullets";
      else if (line === STYLE_TEXT.paragraphs) style = "paragraphs";
      else if (line === LENGTH_TEXT.concise) length = "concise";
      else if (line === LENGTH_TEXT.detailed) length = "detailed";
      else {
        const match = SECTION_LINE.exec(line);
        if (match?.[1])
          guidance.set(match[1].toLowerCase(), {
            instructions: match[3] ?? "",
            optional: match[2] !== "required",
          });
      }
    }
  }
  const sections = (template.headers ?? []).map((heading) => {
    const found = guidance.get(quote(heading).toLowerCase());
    return {
      key: sectionKey(),
      heading,
      instructions: found?.instructions ?? "",
      optional: found?.optional ?? false,
    };
  });
  return {
    name: template.name ?? "",
    general,
    sections,
    style,
    length,
    isDefault: !!template.isDefault,
  };
};

/** What the write API stores for this document. */
export const toTemplateWrite = (
  doc: TemplateDocument,
): {
  name: string;
  headers: string[];
  optionalText: string;
  isDefault: boolean;
} => ({
  name: doc.name.trim(),
  headers: doc.sections
    .map((section) => oneLine(section.heading))
    .filter(Boolean),
  optionalText: toInstructions(doc),
  isDefault: doc.isDefault,
});

export type TemplateProblem =
  | "name"
  | "content"
  | "comma"
  | "headingsTooLong"
  | null;

/**
 * The first reason the document cannot be saved (the API's rules). A
 * template needs instructions or at least one section with a heading
 * (clinical lead, 29/10/2026): instructions alone are enough.
 */
export const templateProblem = (doc: TemplateDocument): TemplateProblem => {
  const write = toTemplateWrite(doc);
  if (!write.name) return "name";
  if (!write.headers.length && !doc.general.trim()) return "content";
  if (write.headers.some((heading) => heading.includes(","))) return "comma";
  if (Array.from(write.headers.join(",")).length > 500)
    return "headingsTooLong";
  return null;
};

/** Same content (ignoring list keys): for the unsaved-changes guard. */
export const sameDocument = (
  a: TemplateDocument,
  b: TemplateDocument,
): boolean =>
  JSON.stringify(toTemplateWrite(a)) === JSON.stringify(toTemplateWrite(b));

/** Moves the item at `from` to `to`. */
export const move = <T>(list: readonly T[], from: number, to: number): T[] => {
  const next = [...list];
  if (from < 0 || from >= next.length || to < 0 || to >= next.length)
    return next;
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item as T);
  return next;
};
